import { useState, useMemo, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { validateIdentifierValue } from "@/lib/products";
import { Modal } from "@/components/base/Modal";

interface Identifier {
  id: string;
  org_id: string;
  variant_id: string | null;
  product_id: string | null;
  type: string;
  value: string;
  created_at: string;
}

interface VariantMini {
  id: string;
  sku: string | null;
  option_values: Record<string, string> | null;
}

interface Props {
  productId: string;
  orgId: string;
  variants: VariantMini[];
}

const ID_TYPES = [
  { value: "barcode", label: "Barcode (Internal)", description: "Internal warehouse scan code" },
  { value: "gtin", label: "GTIN", description: "Global Trade Item Number" },
  { value: "upc", label: "UPC", description: "Universal Product Code (12-digit)" },
  { value: "ean", label: "EAN", description: "European Article Number (13-digit)" },
  { value: "isbn", label: "ISBN", description: "International Standard Book Number" },
  { value: "mpn", label: "MPN", description: "Manufacturer Part Number" },
  { value: "manufacturer_part_number", label: "Manufacturer Part Number", description: "Manufacturer's part number" },
];

interface BulkEntry {
  sku: string;
  value: string;
  variantId: string | null;
  status: "ok" | "invalid" | "sku_not_found" | "duplicate";
  message: string;
}

function parseBulkIdentifiers(
  text: string,
  type: string,
  variants: VariantMini[],
  existingValues: string[],
): BulkEntry[] {
  const bySku = new Map<string, VariantMini>();
  variants.forEach((v) => {
    if (v.sku) bySku.set(v.sku.toLowerCase(), v);
  });
  const seen = new Set(existingValues.map((s) => s.trim()));
  const entries: BulkEntry[] = [];
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    const idx = line.search(/[,;\t]/);
    const sku = idx === -1 ? "" : line.slice(0, idx).trim();
    const value = idx === -1 ? line.trim() : line.slice(idx + 1).trim();
    if (!value) continue;
    const v = bySku.get(sku.toLowerCase());
    if (!v) {
      entries.push({ sku, value, variantId: null, status: "sku_not_found", message: "SKU not found" });
      continue;
    }
    if (seen.has(value)) {
      entries.push({ sku, value, variantId: v.id, status: "duplicate", message: "Duplicate value" });
      continue;
    }
    const valErr = validateIdentifierValue(type, value);
    if (valErr) {
      entries.push({ sku, value, variantId: v.id, status: "invalid", message: `Invalid: ${valErr}` });
      continue;
    }
    seen.add(value);
    entries.push({ sku, value, variantId: v.id, status: "ok", message: "" });
  }
  return entries;
}

const inputCls = "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

export default function IdentifiersTab({ productId, orgId, variants }: Props) {
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState<string | null>(null);
  const [form, setForm] = useState<{ type: string; value: string; variant_id: string; product_id: string }>({
    type: "barcode",
    value: "",
    variant_id: "",
    product_id: productId,
  });

  const { data: identifiers } = useQuery({
    queryKey: ["product-identifiers", orgId, productId],
    enabled: !!orgId && !!productId,
    queryFn: async () => {
      const variantIds = variants.map((v) => v.id);
      const [prodIds, varIds] = await Promise.all([
        supabase.from("product_identifiers")
          .select("id, type, value, product_id, variant_id, created_at")
          .eq("org_id", orgId)
          .eq("product_id", productId)
          .is("variant_id", null)
          .order("type"),
        variantIds.length
          ? supabase.from("product_identifiers")
              .select("id, type, value, product_id, variant_id, created_at")
              .eq("org_id", orgId)
              .in("variant_id", variantIds)
              .order("type")
          : Promise.resolve({ data: [] }),
      ]);
      return {
        productLevel: (prodIds.data ?? []) as Identifier[],
        variantLevel: (varIds.data ?? []) as Identifier[],
      };
    },
  });

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (!form.type || !form.value.trim()) {
      setError("Type and value are required.");
      return;
    }
    const valErr = validateIdentifierValue(form.type, form.value);
    if (valErr) {
      setError(`Invalid ${form.type.toUpperCase()}: ${valErr}.`);
      return;
    }
    setSaving(true);
    const payload: Record<string, unknown> = {
      org_id: orgId,
      type: form.type,
      value: form.value.trim(),
      product_id: productId,
      variant_id: form.variant_id || null,
    };
    const { error: insErr } = await supabase.from("product_identifiers").insert(payload);
    setSaving(false);
    if (insErr) {
      setError(insErr.message);
      return;
    }
    setAddOpen(false);
    setForm({ type: "barcode", value: "", variant_id: "", product_id: productId });
    queryClient.invalidateQueries({ queryKey: ["product-identifiers", orgId, productId] });
  };

  const handleDelete = async (id: string) => {
    setDeleting(id);
    await supabase.from("product_identifiers").delete().eq("id", id).eq("org_id", orgId);
    setDeleting(null);
    queryClient.invalidateQueries({ queryKey: ["product-identifiers", orgId, productId] });
  };

  const variantLabel = (v: VariantMini) =>
    Object.entries(v.option_values ?? {}).map(([k, val]) => `${k}:${val}`).join(" / ") || v.sku || "—";

  const allProductIds = identifiers?.productLevel ?? [];
  const allVariantIds = identifiers?.variantLevel ?? [];

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkType, setBulkType] = useState("gtin");
  const [bulkText, setBulkText] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkResult, setBulkResult] = useState("");
  const [bulkError, setBulkError] = useState("");

  const existingValues = useMemo(
    () => [
      ...(identifiers?.productLevel ?? []).map((i) => i.value),
      ...(identifiers?.variantLevel ?? []).map((i) => i.value),
    ],
    [identifiers],
  );

  const bulkEntries = useMemo(
    () => parseBulkIdentifiers(bulkText, bulkType, variants, existingValues),
    [bulkText, bulkType, variants, existingValues],
  );

  const okCount = bulkEntries.filter((e) => e.status === "ok").length;

  const handleBulkImport = async () => {
    setBulkSaving(true);
    setBulkResult("");
    setBulkError("");
    const ok = bulkEntries.filter((e) => e.status === "ok");
    if (ok.length === 0) {
      setBulkSaving(false);
      setBulkError("No valid entries to import.");
      return;
    }
    const rows = ok.map((e) => ({
      org_id: orgId,
      product_id: productId,
      variant_id: e.variantId,
      type: bulkType,
      value: e.value,
    }));
    const { error } = await supabase.from("product_identifiers").insert(rows);
    setBulkSaving(false);
    if (error) {
      setBulkError(error.message);
      return;
    }
    setBulkResult(`Imported ${ok.length} identifier${ok.length === 1 ? "" : "s"}.`);
    setBulkText("");
    queryClient.invalidateQueries({ queryKey: ["product-identifiers", orgId, productId] });
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Product identifiers</h3>
            <p className="text-xs text-slate-400 mt-0.5">Enter a real GTIN, UPC or EAN if the product has one. Never invent a barcode. Product-level and variant-level codes are distinct. “No barcode” in Tradivo is not Amazon exemption.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setBulkError(""); setBulkResult(""); setBulkText(""); setBulkOpen(true); }}
              className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
            >
              <i className="ri-file-list-3-line" /> Bulk import
            </button>
            <button
              onClick={() => { setError(""); setAddOpen(true); }}
              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
            >
              <i className="ri-add-line" /> Add identifier
            </button>
          </div>
        </div>

        {/* Info: barcode vs. retail distinction */}
        <div className="px-4 py-3 bg-amber-50/50 border-b border-amber-100">
          <p className="text-xs text-amber-700">
            <span className="font-semibold">Barcode</span> = internal warehouse scanning (maps to <code className="font-mono bg-amber-100 px-1 rounded">variants.barcode</code>).{" "}
            <span className="font-semibold">GTIN/UPC/EAN</span> = official retail identifiers used on marketplaces. Do not type N/A or dummy zeros. Amazon GTIN exemption is not granted from this screen.
          </p>
        </div>

        {/* Product-level */}
        <div className="p-4">
          <p className="text-xs font-semibold text-slate-600 mb-2">Product level</p>
          {allProductIds.length === 0 ? (
            <p className="text-xs text-slate-400">No product-level identifiers.</p>
          ) : (
            <div className="space-y-1">
              {allProductIds.map((id) => (
                <div key={id.id} className="flex items-center gap-3 py-1.5">
                  <span className="text-xs font-semibold bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full w-20 text-center">{id.type.toUpperCase()}</span>
                  <span className="text-sm font-mono text-slate-800">{id.value}</span>
                  <span className="text-xs text-slate-400">{ID_TYPES.find((t) => t.value === id.type)?.description}</span>
                  <div className="ml-auto">
                    <button
                      onClick={() => handleDelete(id.id)}
                      disabled={deleting === id.id}
                      className="w-7 h-7 flex items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-red-500"
                    >
                      <i className="ri-delete-bin-line text-xs" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Variant-level */}
        {variants.length > 0 && (
          <div className="p-4 border-t border-slate-100">
            <p className="text-xs font-semibold text-slate-600 mb-3">Variant level</p>
            {allVariantIds.length === 0 ? (
              <p className="text-xs text-slate-400">No variant-level identifiers.</p>
            ) : (
              <div className="space-y-1">
                {allVariantIds.map((id) => {
                  const v = variants.find((v) => v.id === id.variant_id);
                  return (
                    <div key={id.id} className="flex items-center gap-3 py-1.5">
                      <span className="text-xs font-semibold bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full w-20 text-center">{id.type.toUpperCase()}</span>
                      <span className="text-sm font-mono text-slate-800">{id.value}</span>
                      <span className="text-xs text-slate-400 font-mono">{v ? variantLabel(v) : "—"}</span>
                      <div className="ml-auto">
                        <button
                          onClick={() => handleDelete(id.id)}
                          disabled={deleting === id.id}
                          className="w-7 h-7 flex items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-red-500"
                        >
                          <i className="ri-delete-bin-line text-xs" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add Identifier"
        footer={
          <>
            <button onClick={() => setAddOpen(false)} className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm whitespace-nowrap">Cancel</button>
            <button onClick={handleAdd} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm whitespace-nowrap disabled:opacity-60">
              {saving ? "Saving..." : "Add Identifier"}
            </button>
          </>
        }
      >
        <form onSubmit={handleAdd} className="space-y-4">
          {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
          <div>
            <label className={labelCls}>Identifier type *</label>
            <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} className={inputCls}>
              {ID_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label} — {t.description}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Value *</label>
            <input
              required
              value={form.value}
              onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
              className={inputCls}
              placeholder={form.type === "ean" ? "1234567890123" : form.type === "upc" ? "123456789012" : "Enter identifier..."}
            />
          </div>
          <div>
            <label className={labelCls}>Assign to variant (or leave blank for product level)</label>
            <select value={form.variant_id} onChange={(e) => setForm((f) => ({ ...f, variant_id: e.target.value }))} className={inputCls}>
              <option value="">Product level</option>
              {variants.map((v) => (
                <option key={v.id} value={v.id}>{variantLabel(v)}</option>
              ))}
            </select>
          </div>
          {form.type === "barcode" && (
            <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
              <i className="ri-information-line mr-1" />
              The primary warehouse barcode is stored on <code className="font-mono bg-amber-100 px-1 rounded">variants.barcode</code> for high-performance scanning. Recording it here as well creates a searchable cross-reference for marketplace purposes.
            </div>
          )}
        </form>
      </Modal>

      <Modal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title="Bulk Import Identifiers"
        footer={
          <>
            <button onClick={() => setBulkOpen(false)} className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm whitespace-nowrap">Cancel</button>
            <button onClick={handleBulkImport} disabled={bulkSaving || okCount === 0} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm whitespace-nowrap disabled:opacity-60">
              {bulkSaving ? "Importing..." : `Import ${okCount} identifier${okCount === 1 ? "" : "s"}`}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {bulkError && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{bulkError}</div>}
          {bulkResult && <div className="px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-lg text-sm text-emerald-700">{bulkResult}</div>}
          <div>
            <label className={labelCls}>Identifier type *</label>
            <select value={bulkType} onChange={(e) => setBulkType(e.target.value)} className={inputCls}>
              {ID_TYPES.filter((t) => t.value !== "barcode").map((t) => (
                <option key={t.value} value={t.value}>{t.label} — {t.description}</option>
              ))}
            </select>
            <p className="text-xs text-slate-400 mt-1">Internal barcodes are stored on the variant, not here.</p>
          </div>
          <div>
            <label className={labelCls}>Paste one per line — SKU, value</label>
            <textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              rows={8}
              className={inputCls}
              placeholder={"RTS-001-BLK-M, 0123456789012\nRTS-001-BLK-L, 0123456789029"}
            />
            <p className="text-xs text-slate-400 mt-1">Each line maps a variant by its SKU to a value. Values are check-digit validated against the selected type.</p>
          </div>
          {bulkEntries.length > 0 && (
            <div className="border border-slate-200 rounded-lg overflow-hidden max-h-56 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-slate-500 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 font-medium">SKU</th>
                    <th className="px-3 py-2 font-medium">Value</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {bulkEntries.map((e, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 font-mono text-xs text-slate-700">{e.sku || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-700">{e.value}</td>
                      <td className="px-3 py-2 text-xs">
                        {e.status === "ok" && <span className="text-emerald-700">Ready</span>}
                        {e.status === "invalid" && <span className="text-red-600">{e.message}</span>}
                        {e.status === "sku_not_found" && <span className="text-amber-600">{e.message}</span>}
                        {e.status === "duplicate" && <span className="text-amber-600">{e.message}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}